// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Le Verifiche: chi controlla i controlli"
// Centro assistenza → guida per amministratori e responsabili (HACCP).
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// ATTENZIONE, ERRORE FACILE: "verificatore" NON e' un permesso che si spunta
// nell'anagrafica operatori. E' CALCOLATO AL LOGIN dallo scadenziario
// (routes/organico.js): sei verificatore se almeno una voce non obsoleta ti
// nomina nel campo "Verifica" o fra i "Verificatori aggiuntivi". Stessa cosa
// per "monitoratore" con monitoraggio/supplenti. Chi scrive "si attiva dal
// profilo" sta sbagliando (e' gia' successo nella prima bozza della guida #4).
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Menu ........... "Le mie verifiche" (sottoetichetta "Attività da
//                    verificare"), spenta se operatore.verificatore e' falso.
//   Schermata ...... App.js, Verifiche — titolo "Verifiche da eseguire";
//                    linguette "Verifiche singole" (con contatore) e
//                    "Verifica a blocchi".
//   Card ........... chip esito (Conforme/Non conforme/N.A.) + data + chi ha
//                    registrato; riquadro "Valore: X · min · max" se c'e';
//                    pulsante "Confermo la verifica".
//   Coda vuota ..... "Nessuna verifica in attesa / Tutte le attività di tua
//                    competenza risultano già verificate."
//   Designazione ... scadenziario, campi "Verifica" e "Verificatori
//                    aggiuntivi (opzionale, multipli)".
//   Blocchi ........ VerificaBlocco: "Parametri verifica a blocchi" — Ambito*,
//                    Tipo attività*, Area / reparto, Responsabile assegnato,
//                    Dal*, Al*, Tipo verifica* (on-site/documentale/generica,
//                    multipli), Note, "Verifica disponibilità" → "Riepilogo
//                    anteprima" (Da verificare / Di cui NC / Già verificate)
//                    → "Conferma verifica a blocchi (N attività)".
//                    Il "Responsabile assegnato" e' quello A SCADENZIARIO, non
//                    chi ha firmato la singola riga: i giorni coperti da un
//                    supplente restano dentro al blocco (voluto).
//   Storico blocchi. tabella: Data verifica, Verificatore, Ambito, Tipo,
//                    Area / Responsabile ("Tutte" se senza filtro), Periodo,
//                    Totale, NC, Tipo verifica, Note.
//   Effetto ........ una registrazione verificata NON e' piu' correggibile
//                    dall'operatore (vedi guida #4 e PUT /attivita/:id).
//   Firma .......... dal 19/08/2026 il backend firma col token e accetta la
//                    verifica solo dal designato/aggiuntivo/amministratore.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'v0', label: 'Chi controlla i controlli' },
  { id: 'v1', label: '1. Chi è verificatore' },
  { id: 'v2', label: '2. La coda delle verifiche' },
  { id: 'v3', label: '3. Confermare una verifica' },
  { id: 'v4', label: '4. La verifica a blocchi' },
  { id: 'v5', label: '5. Leggere l’anteprima' },
  { id: 'v6', label: 'Le trappole' },
  { id: 'v7', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro', pieno }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999,
      background: pieno ? c.fg : c.bg, color: pieno ? '#fff' : c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const BtnFinto = ({ children, tono = 'neutro', pieno, block }) => {
  const c = { verde: LT.verde, blu: LT.blu, neutro: LT.n400 }[tono];
  return (
    <span style={{
      display: block ? 'flex' : 'inline-flex', alignItems: 'center',
      justifyContent: 'center', gap: 6,
      padding: '9px 14px', borderRadius: LT.r.md, fontSize: 13, fontWeight: 700,
      background: pieno ? c : LT.n0,
      color: pieno ? '#fff' : (tono === 'neutro' ? LT.n700 : c),
      border: `1px solid ${pieno || tono !== 'neutro' ? c : LT.n300}`,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

// Le due linguette della schermata (Verifiche singole / Verifica a blocchi).
const Linguette = ({ attiva = 0 }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
    {[['Verifiche singole', 2], ['Verifica a blocchi', null]].map(([testo, n], i) => {
      const on = i === attiva;
      return (
        <span key={testo} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 999,
          border: `1px solid ${on ? LT.blu : LT.n200}`,
          background: on ? LT.blu : LT.n0, color: on ? '#fff' : LT.n600,
        }}>
          {testo}
          {n != null && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
              background: on ? 'rgba(255,255,255,.22)' : LT.bluSoft,
              color: on ? '#fff' : LT.blu,
            }}>{n}</span>
          )}
        </span>
      );
    })}
  </div>
);

// La card di una registrazione in attesa di verifica.
const CardDaVerificare = ({ esito, data, operatore, oggetto, dove, tipo, valore }) => {
  const conf = {
    conforme:    { chip: 'Conforme',     tono: 'verde', bordo: LT.blu },
    nonconforme: { chip: 'Non conforme', tono: 'rosso', bordo: LT.rosso },
  }[esito];
  return (
    <div style={{
      background: LT.n0, border: `1px solid ${LT.n150}`,
      borderLeft: `4px solid ${conf.bordo}`,
      borderRadius: LT.r.lg, padding: 14, marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <ChipFinto tono={conf.tono} pieno>{conf.chip}</ChipFinto>
        <ChipFinto>{data}</ChipFinto>
        <ChipFinto tono="blu">{operatore}</ChipFinto>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{
          width: 40, height: 40, borderRadius: LT.r.md, flexShrink: 0,
          background: LT.cat.temperature.bg, color: LT.cat.temperature.c,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Ico d={I[LT.cat.temperature.icon] || I.list} size={20} stroke={LT.cat.temperature.c} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{oggetto}</div>
          <div style={{ fontSize: 12.5, color: LT.n600, marginTop: 3 }}>
            {dove} <span style={{ color: LT.n300 }}>·</span> {tipo}
          </div>
        </div>
      </div>
      {valore && (
        <div style={{
          marginTop: 10, borderRadius: LT.r.md, padding: '8px 12px',
          fontSize: 13, fontFamily: LT.mono,
          background: esito === 'conforme' ? LT.bluSoft : LT.rossoSoft,
          color: esito === 'conforme' ? LT.blu : LT.rosso,
          border: `1px solid ${esito === 'conforme' ? '#bfdbfe' : '#fecaca'}`,
        }}>
          Valore: <strong>{valore}</strong> · min 0 · max 4
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <BtnFinto tono="verde" pieno block>Confermo la verifica</BtnFinto>
      </div>
    </div>
  );
};

// Un riquadro-numero dell'anteprima blocchi.
const KpiFinto = ({ etichetta, numero, tono = 'neutro' }) => {
  const c = {
    blu:    { fg: LT.blu,   bg: LT.bluSoft,   bordo: '#bfdbfe' },
    rosso:  { fg: LT.rosso, bg: LT.rossoSoft, bordo: '#fecaca' },
    neutro: { fg: LT.n500,  bg: LT.n50,       bordo: LT.n200 },
  }[tono];
  return (
    <div style={{
      flex: 1, minWidth: 110, background: c.bg, border: `1px solid ${c.bordo}`,
      borderRadius: LT.r.md, padding: '12px 14px', textAlign: 'center',
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, color: c.fg,
        textTransform: 'uppercase', letterSpacing: '.06em',
      }}>{etichetta}</div>
      <div style={{
        fontSize: 25, fontWeight: 700, color: c.fg === LT.n500 ? LT.n700 : c.fg,
        lineHeight: 1.1, marginTop: 4, fontFamily: LT.mono,
      }}>{numero}</div>
    </div>
  );
};

export default function Verifiche() {
  return (
    <GuidaLayout
      titolo="Le Verifiche: chi controlla i controlli"
      sottotitolo="La firma di seconda mano che chiude il cerchio dell'autocontrollo: chi può darla, come si dà su una registrazione sola e come si dà su un mese intero in una volta."
      durata="~ 10 minuti"
      destinatario="Per amministratori e responsabili"
      sezioni={SEZIONI}
    >

      {/* v0 */}
      <H id="v0">Chi controlla i controlli</H>

      <P>
        Il piano HACCP distingue due gesti. Il <Strong>monitoraggio</Strong> è la
        registrazione di ogni giorno: l'operatore legge la temperatura e la scrive. La{' '}
        <Strong>verifica</Strong> è il secondo sguardo: una persona diversa — di norma il
        responsabile — ripassa quelle registrazioni e conferma che il sistema sta
        funzionando. È il principio dei quattro occhi, ed è una delle prime cose che un
        ispettore va a cercare: non solo <em>se</em> i controlli sono stati fatti, ma{' '}
        <em>chi li ha riletti</em>.
      </P>
      <P>
        In LabThree la verifica è una <Strong>firma</Strong>: resta scritta con nome, data e
        ora accanto a ogni registrazione verificata, e nelle stampe dello Storico compare
        come spunta con la firma del verificatore in calce.
      </P>

      <Callout kind="info" titolo="Verificare non è correggere">
        La verifica dice «ho visto, va bene». Se rileggendo trovate un errore, la strada non
        è verificarlo: è correggerlo prima, dallo <Code>Storico</Code>, e verificarlo poi.
        Anzi — una volta verificata, la registrazione <Strong>non è più correggibile
        dall'operatore</Strong>: la correzione diventa riservata agli amministratori.
      </Callout>

      {/* v1 */}
      <H id="v1" cat="formazione">1. Chi è verificatore</H>

      <P>
        Verificatore <Strong>non è un permesso da spuntare</Strong> su una scheda: lo si
        diventa dallo <Code>Scadenziario</Code>. Ogni attività programmata ha un campo{' '}
        <Strong>Verifica</Strong> dove si indica chi deve rileggerla — e siete verificatori
        se almeno un'attività vi nomina lì.
      </P>

      <Passo n="1" titolo="Il verificatore designato">
        <P style={{ margin: '4px 0' }}>
          Nella voce di scadenziario, campo <Strong>Verifica</Strong>: una persona sola,
          quella che ha in carico il secondo sguardo su quell'attività.
        </P>
      </Passo>

      <Passo n="2" titolo="I verificatori aggiuntivi">
        <P style={{ margin: '4px 0' }}>
          Subito sotto, <Strong>Verificatori aggiuntivi (opzionale, multipli)</Strong>: i
          sostituti. Vedono la stessa coda del designato e possono firmare al posto suo —
          serve per ferie e malattie, così le registrazioni non restano in attesa per
          settimane.
        </P>
      </Passo>

      <Callout kind="warn" titolo="La voce «Le mie verifiche» è grigia?">
        Come per le attività, la voce di menu si accende da sola quando lo scadenziario vi
        nomina da qualche parte. Il controllo avviene <Strong>all'ingresso</Strong>: se
        siete appena stati designati e la voce è ancora spenta, uscite e rientrate col PIN.
      </Callout>

      {/* v2 */}
      <H id="v2" cat="formazione">2. La coda delle verifiche</H>

      <MockFrame
        url="app.labthree.it — Verifiche da eseguire"
        markers={[
          { n: 1, top: '17.7%', left: '48%', titolo: 'Due linguette',
            testo: 'Verifiche singole, col conteggio di quante aspettano, e Verifica a blocchi per chiudere un periodo intero (capitolo 4).' },
          { n: 2, top: '27.8%', left: '52%', titolo: 'Chi e quando',
            testo: 'Ogni scheda dice l’esito registrato, la data e chi ha firmato la registrazione. State rileggendo il lavoro di quella persona in quel giorno.' },
          { n: 3, top: '43.5%', left: '48%', titolo: 'Il valore coi limiti',
            testo: 'Se il monitoraggio aveva un valore misurato, lo vedete accanto ai limiti: la verifica si fa con i numeri davanti, non sulla fiducia.' },
        ]}
      >
        <Schermo>
          <div style={{
            fontSize: 10.5, color: LT.n500, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '.08em',
          }}>Mercoledì 20 agosto</div>
          <div style={{ fontSize: 19, fontWeight: 700, color: LT.n900, marginTop: 3 }}>
            Verifiche da eseguire
          </div>
          <div style={{ fontSize: 12, color: LT.n500, marginBottom: 12 }}>Salumificio Demo S.r.l.</div>

          <div style={{ marginBottom: 14 }}>
            <Linguette attiva={0} />
          </div>

          <CardDaVerificare
            esito="nonconforme"
            data="19/08/2026 · 06:10"
            operatore="Mario Rossi"
            oggetto="Cella 1 — sale"
            dove="Reparto salatura"
            tipo="Controllo temperatura"
            valore="6,8"
          />
          <CardDaVerificare
            esito="conforme"
            data="19/08/2026 · 06:05"
            operatore="Mario Rossi"
            oggetto="Cella 2 — stagionatura"
            dove="Reparto stagionatura"
            tipo="Controllo temperatura"
            valore="2,4"
          />
        </Schermo>
      </MockFrame>

      <P>
        La coda mostra <Strong>solo le attività di vostra competenza</Strong>: quelle il cui
        scadenziario vi nomina come verificatore o come aggiuntivo, e che nessuno ha ancora
        verificato. Il bordo colorato a sinistra ripete l'esito: blu per le conformi, rosso
        per le non conformi — che così saltano all'occhio per prime.
      </P>
      <P>
        Quando la coda è vuota la schermata lo dice con la spunta verde:{' '}
        <em>«Nessuna verifica in attesa — tutte le attività di tua competenza risultano già
        verificate»</em>. È la schermata che si vuole vedere.
      </P>

      {/* v3 */}
      <H id="v3" cat="formazione">3. Confermare una verifica</H>

      <P>
        Su ogni scheda c'è un solo pulsante: <Tasto>Confermo la verifica</Tasto>. Un tocco,
        e la registrazione risulta verificata con il vostro nome e l'ora di adesso. Non c'è
        una finestra di conferma: il gesto è pensato per scorrere una coda di venti
        registrazioni in un paio di minuti.
      </P>

      <Callout kind="warn" titolo="La firma è la vostra, e chiude la registrazione">
        Due conseguenze da conoscere prima di premere. La prima: la firma che finisce agli
        atti — e sulle stampe — è quella di <Strong>chi preme</Strong>, non del verificatore
        designato: se firmate come sostituti, risulta il vostro nome, ed è giusto così. La
        seconda: da quel momento l'operatore <Strong>non può più correggere</Strong> quella
        registrazione da solo. Se vi accorgete di un valore strano, prima si sistema, poi si
        firma.
      </Callout>

      <Callout kind="tip" titolo="Le non conformi meritano un attimo in più">
        Su una riga rossa, la domanda della verifica non è «il valore è fuori?» — quello si
        sa già. È «la non conformità è stata gestita?». Un salto nel registro NC per vedere
        che la pratica sia aperta e trattata vale più della firma stessa.
      </Callout>

      {/* v4 */}
      <H id="v4" cat="formazione">4. La verifica a blocchi</H>

      <P>
        Firmare una per una va bene per il ritmo quotidiano. Ma se le verifiche si fanno a
        fine mese, la coda può contenere centinaia di righe: per questo c'è la seconda
        linguetta, <Tasto>Verifica a blocchi</Tasto> — si sceglie <Strong>cosa</Strong> e{' '}
        <Strong>quale periodo</Strong>, e si firma tutto l'insieme in un colpo.
      </P>

      <Passo n="1" titolo="Delimitare il blocco">
        <P style={{ margin: '4px 0' }}>
          <Strong>Ambito</Strong> e <Strong>Tipo attività</Strong> sono obbligatori (le
          temperature, le pulizie…), <Strong>Dal / Al</Strong> è il periodo. Chi ha reparti
          con responsabili diversi può restringere anche per <Strong>Area / reparto</Strong>{' '}
          o per <Strong>Responsabile assegnato</Strong> e verificare una zona alla volta.
        </P>
      </Passo>

      <Passo n="2" titolo="Dire che verifica è">
        <P style={{ margin: '4px 0' }}>
          <Strong>Tipo verifica</Strong>: <Code>on-site</Code> (siete andati a vedere),{' '}
          <Code>documentale</Code> (avete riletto le carte), <Code>generica</Code>. Si può
          sceglierne più d'una, e finisce scritta nel registro dei blocchi.
        </P>
      </Passo>

      <Passo n="3" titolo="Guardare prima di firmare">
        <P style={{ margin: '4px 0' }}>
          <Tasto>Verifica disponibilità</Tasto> non firma niente: conta cosa c'è nel blocco
          e ve lo mostra. La firma arriva solo dopo, con{' '}
          <Tasto>Conferma verifica a blocchi</Tasto>, che dice anche quante attività sta per
          coprire.
        </P>
      </Passo>

      <Callout kind="info" titolo="Il «Responsabile assegnato» è quello a scadenziario">
        Filtrando per responsabile, il blocco prende le attività che lo scadenziario{' '}
        <Strong>assegna</Strong> a quella persona — non quelle che ha materialmente firmato.
        È voluto: i giorni in cui un supplente ha coperto il titolare restano dentro al
        blocco del titolare, invece di cadere in una terra di nessuno.
      </Callout>

      {/* v5 */}
      <H id="v5" cat="formazione">5. Leggere l'anteprima</H>

      <MockFrame
        url="app.labthree.it — Riepilogo anteprima"
        didascalia="Tre numeri e, sotto, il pulsante che firma. Se fra le righe ci sono non conformità, il programma suggerisce di guardarle una per una prima di firmare l'insieme."
      >
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`,
            borderLeft: `4px solid ${LT.blu}`, borderRadius: LT.r.lg, padding: 16,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: LT.n600,
              textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12,
            }}>Riepilogo anteprima</div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <KpiFinto etichetta="Da verificare" numero="27" tono="blu" />
              <KpiFinto etichetta="Di cui NC" numero="1" tono="rosso" />
              <KpiFinto etichetta="Già verificate" numero="4" tono="neutro" />
            </div>

            <div style={{
              background: LT.arancioSoft, border: '1px solid #fed7aa',
              borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 12,
              display: 'flex', gap: 10, alignItems: 'flex-start',
              fontSize: 12.5, color: LT.n700, lineHeight: 1.45,
            }}>
              <Ico d={I.alert} size={17} stroke={LT.arancio} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong style={{ color: LT.arancio }}>Attenzione:</strong> sono presenti{' '}
                <strong>1</strong> non conformità. Si consiglia di verificarle singolarmente
                prima di procedere con la verifica a blocchi.
              </span>
            </div>

            <BtnFinto tono="verde" pieno block>Conferma verifica a blocchi (27 attività)</BtnFinto>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        <Strong>Da verificare</Strong> è quante righe la firma coprirà;{' '}
        <Strong>Di cui NC</Strong> quante di queste sono non conformi;{' '}
        <Strong>Già verificate</Strong> quante, nello stesso periodo, portano già una firma
        e quindi restano fuori. Se avete ristretto per area o responsabile, un riquadro in
        più avvisa quante attività dello stesso periodo restano <Strong>fuori dal
        filtro</Strong> — così tre reparti verificati su quattro non passano per un periodo
        chiuso.
      </P>
      <P>
        Ogni blocco confermato finisce nello <Strong>Storico verifiche a blocchi</Strong>,
        in fondo alla stessa pagina: data, verificatore, cosa copriva, quante attività e
        quante NC. È il documento da mostrare quando chiedono «e le verifiche chi le fa?».
      </P>

      {/* v6 */}
      <H id="v6">Le trappole</H>

      <Callout kind="warn" titolo="Firmare il blocco con le NC dentro">
        Il programma lo permette — a volte è legittimo, se le pratiche sono già chiuse — ma
        l'avviso arancione c'è per una ragione: una NC verificata «in mezzo al mucchio»
        senza che nessuno abbia guardato la pratica è il punto debole che un ispettore trova
        al primo colpo. Prima le righe rosse una per una, poi il blocco.
      </Callout>

      <Callout kind="warn" titolo="Il blocco non copre quello che non vede">
        Il conteggio dell'anteprima vale per <Strong>quel</Strong> filtro: ambito, tipo,
        periodo, eventuale area. Cambiare filtro azzera l'anteprima, ed è voluto — la firma
        deve riferirsi esattamente a ciò che avete appena contato. Se il riquadro grigio
        dice che restano attività fuori dal filtro, servono altri blocchi per chiuderle.
      </Callout>

      <Callout kind="warn" titolo="Verificare se stessi">
        Il programma non vieta di essere insieme monitoratore e verificatore della stessa
        attività — nelle aziende piccole a volte non c'è alternativa. Ma dove le persone ci
        sono, il campo Verifica dovrebbe indicare <Strong>qualcun altro</Strong> rispetto a
        chi monitora: due firme uguali una sopra l'altra valgono poco, agli occhi di
        chiunque controlli.
      </Callout>

      {/* v7 */}
      <H id="v7">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Ho verificato una riga per sbaglio. Posso togliere la firma?">
          No, dalla schermata non si può: la verifica è pensata come definitiva. Se è
          successo, la registrazione sotto resta comunque correggibile da un
          amministratore, dallo Storico. Se il caso capita spesso, il problema è di solito
          la fretta nello scorrere la coda.
        </FAQ>

        <FAQ q="Il verificatore designato è in ferie: le registrazioni restano lì?">
          Solo se non gli avete messo nessun sostituto. I <Strong>Verificatori
          aggiuntivi</Strong> nello scadenziario vedono la stessa coda e firmano al posto
          suo, col proprio nome. Aggiungerli richiede un minuto e vi salva agosto.
        </FAQ>

        <FAQ q="Che differenza c'è fra verificare dalla coda e verificare a blocchi?">
          Nessuna nel risultato sulla singola riga: firma, data e ora. Cambia la
          granularità del gesto — la coda è riga per riga, il blocco copre un periodo e
          resta agli atti anche come documento suo, nello storico dei blocchi, con ambito,
          periodo e tipo di verifica dichiarato.
        </FAQ>

        <FAQ q="Le attività saltate (N.A.) finiscono in verifica?">
          No: la coda mostra le registrazioni con un esito, conforme o non conforme. Un
          N.A. dice che quel giorno il controllo non si poteva fare, e non c'è un risultato
          da confermare.
        </FAQ>

        <FAQ q="Nelle stampe si vede chi ha verificato?">
          Sì. Nelle stampe dello Storico ogni riga verificata porta la spunta e, quando i
          verificatori del periodo sono più d'uno, un richiamo numerico che rimanda ai
          blocchi firma in calce — con la firma grafica, se la persona l'ha caricata sul
          proprio profilo.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Lo Storico e le stampe</Strong>: dove le registrazioni verificate
            diventano il registro da mettere sul tavolo quando arriva l'ispezione.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
